import {
  pickInvoiceStatusForOutput,
  STATUS_WITHHELD,
  STATUS_COMPLETED_AUTO,
  CHARGED_AS_DOLLARS,
  CHARGED_AS_INVOICE
} from '@core/shared/utils/financeLedger';

// Slice `lineItems` into billables. The deliverable resolves to:
//   • credit-funded master (PRO submission): a single chargedAs:'credit'
//     billable carrying isPrimary, amount = computeOutputCreditAmount.
//   • dollar-funded iteration / additional output: the dollar/invoice
//     split with isPrimary on the first emitted row.
// Addons always emit as dollars/invoice rows with isPrimary:false.
//
// `output` is threaded through purely to pick the invoice-pathway status
// (ADR-0021): pre-completion bill-to-org rows land as `pending delivery`
// and only flip to `invoice` once the output is approved server-side. See
// pickInvoiceStatusForOutput in @core/shared/utils/financeLedger.
//
// Pure function — caller supplies the selected team context so this is
// testable without standing up the PaymentsInvoice class instance.
function buildBillablesFromLineItems({
  lineItems,
  dollarsPortion,
  invoicePortion,
  output,
  selectedTeamId,
  selectedTeamName
}) {
  const billables = [];
  let dollarsRemaining = dollarsPortion;
  const invoiceStatus = pickInvoiceStatusForOutput(output);

  // Free ($0) addons carry no charge but must still surface on the finance
  // statement as a line item. They're emitted after the priced rows so their
  // status can mirror the paid dollar lines: a free addon bundled with a paid
  // dollar charge rides that charge's approval (`withheld`), while a free addon
  // submitted on its own has nothing to approve and settles immediately
  // (`completed (auto)`, which also clears finance's dollar-completion auth gate
  // — that only trips on the exact status `completed`).
  const freeAddons = [];

  lineItems.forEach((item) => {
    if (item.isDeliverable && item.credits > 0) {
      const teamAllocations = selectedTeamId
        ? [
            {
              teamId: selectedTeamId,
              teamName: selectedTeamName || selectedTeamId,
              amount: item.credits
            }
          ]
        : null;
      billables.push({
        isPrimary: true,
        type: item.type,
        desc: item.desc,
        chargedAs: 'credit',
        amount: item.credits,
        status: 'completed',
        ...(teamAllocations ? { teamAllocations } : {})
      });
      return;
    }

    // Iteration / additional output (item.isDeliverable, credits === 0)
    // or addons. isPrimary anchors to the deliverable line via
    // isDeliverable; the first emitted dollar/invoice row carries it.
    const toDollar = Math.min(item.cost, dollarsRemaining);
    const toInvoice = item.cost - toDollar;
    dollarsRemaining -= toDollar;
    let emittedForItem = false;
    if (toDollar > 0) {
      billables.push({
        isPrimary: item.isDeliverable,
        type: item.type,
        desc: item.desc,
        chargedAs: 'dollars',
        amount: toDollar,
        status: 'withheld'
      });
      emittedForItem = true;
    }
    if (toInvoice > 0) {
      billables.push({
        isPrimary: item.isDeliverable && !emittedForItem,
        type: item.type,
        desc: item.desc,
        chargedAs: 'invoice',
        amount: toInvoice,
        status: invoiceStatus
      });
      emittedForItem = true;
    }
    // A non-deliverable addon that emitted no priced row is free ($0).
    if (!emittedForItem && !item.isDeliverable) {
      freeAddons.push(item);
    }
  });

  // A free addon mirrors the pathway of the paid lines it ships with so it
  // settles on the same lifecycle and lands as a line item on the matching
  // statement: a paid dollar line → ride its approval (`dollars`/`withheld`);
  // otherwise a paid invoice line → ride the invoice (`invoice`/invoiceStatus,
  // so an invoiced project shows it as an invoice line item); otherwise
  // (credit-only project) there is nothing to ride, so settle it immediately
  // (`dollars`/`completed (auto)`).
  const hasPaidDollar = billables.some(
    (b) => b.chargedAs === CHARGED_AS_DOLLARS && b.amount > 0
  );
  const hasPaidInvoice = billables.some(
    (b) => b.chargedAs === CHARGED_AS_INVOICE && b.amount > 0
  );
  freeAddons.forEach((item) => {
    const base = {
      isPrimary: false,
      type: item.type,
      desc: item.desc,
      amount: 0
    };
    if (hasPaidDollar) {
      billables.push({
        ...base,
        chargedAs: CHARGED_AS_DOLLARS,
        status: STATUS_WITHHELD
      });
    } else if (hasPaidInvoice) {
      billables.push({
        ...base,
        chargedAs: CHARGED_AS_INVOICE,
        status: invoiceStatus
      });
    } else {
      billables.push({
        ...base,
        chargedAs: CHARGED_AS_DOLLARS,
        status: STATUS_COMPLETED_AUTO
      });
    }
  });

  // Defensive: with buildLineItemsForOutput always emitting the
  // deliverable, every payload carries at least one billable. Bail
  // only if we still produced nothing while the caller expects
  // invoice coverage — indicates the caller skipped the output.
  if (billables.length === 0 && invoicePortion > 0) {
    return [];
  }
  return billables;
}

export default buildBillablesFromLineItems;
